import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Bot, Check, Sparkles, Clock, ExternalLink, Plus, Upload, Trash2, BookOpen, GraduationCap, UserCog, Activity, X, Send } from "lucide-react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Link } from "react-router-dom";

const PRIORITY_COLOR = {
  high: "border-red-500 text-red-700 bg-red-50",
  normal: "border-neutral-400 text-neutral-700 bg-neutral-50",
  low: "border-neutral-300 text-neutral-500 bg-neutral-50",
};

const SOURCE_LABEL = {
  manual: "Manual",
  upload: "Uploaded",
  chat: "From chat",
  admin_answer: "Admin taught",
};

export default function BotPage() {
  return (
    <div className="px-6 py-8 sm:px-10 sm:py-10">
      <header className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">Automation</div>
        <h1 className="mt-2 flex items-center gap-3 font-display text-4xl font-black tracking-tight sm:text-5xl" data-testid="bot-title">
          <Bot className="h-10 w-10" /> Auto-responder
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          Powered by Claude Sonnet 4.5. Teach it with a knowledge base, and it will grow smarter every time you answer a training question or mark a chat reply as good.
        </p>
      </header>

      <Tabs defaultValue="human">
        <TabsList className="flex-wrap rounded-none">
          <TabsTrigger value="human" data-testid="tab-human"><UserCog className="mr-1.5 h-3.5 w-3.5" />Human Requests</TabsTrigger>
          <TabsTrigger value="train" data-testid="tab-train"><GraduationCap className="mr-1.5 h-3.5 w-3.5" />Train Bot</TabsTrigger>
          <TabsTrigger value="kb" data-testid="tab-kb"><BookOpen className="mr-1.5 h-3.5 w-3.5" />Knowledge Base</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings"><Sparkles className="mr-1.5 h-3.5 w-3.5" />Settings</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity"><Activity className="mr-1.5 h-3.5 w-3.5" />Activity</TabsTrigger>
        </TabsList>
        <TabsContent value="human" className="mt-6"><RequestsPanel kind="human" /></TabsContent>
        <TabsContent value="train" className="mt-6"><TrainPanel /></TabsContent>
        <TabsContent value="kb" className="mt-6"><KnowledgePanel /></TabsContent>
        <TabsContent value="settings" className="mt-6"><SettingsPanel /></TabsContent>
        <TabsContent value="activity" className="mt-6"><ActivityPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Human Requests panel (previously "Requests")
// ---------------------------------------------------------------------------
function RequestsPanel({ kind }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filter !== "all") params.set("status_filter", filter);
    if (kind) params.set("kind", kind);
    api.get(`/bot/requests?${params.toString()}`)
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter, kind]);

  const markResolved = async (id) => {
    try {
      await api.patch(`/bot/requests/${id}`, { status: "resolved" });
      toast.success("Marked resolved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {["open", "resolved", "all"].map((f) => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? "default" : "outline"}
              onClick={() => setFilter(f)}
              className="h-8 rounded-sm text-xs capitalize"
              data-testid={`req-filter-${f}`}
            >
              {f}
            </Button>
          ))}
        </div>
        <div className="text-xs text-muted-foreground">
          These need a real staff member (bookings, rescheduling, complaints, etc.)
        </div>
      </div>
      <div className="border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Since</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Customer</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Topic</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Summary</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Priority</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Status</TableHead>
              <TableHead className="text-right text-[10px] font-bold uppercase tracking-[0.2em]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="py-16 text-center text-muted-foreground">
                No {filter === "all" ? "" : filter} requests. When the bot escalates, they'll appear here.
              </TableCell></TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} className="border-border" data-testid={`req-row-${r.id}`}>
                  <TableCell className="whitespace-nowrap font-mono text-[10px] text-muted-foreground">
                    {r.opened_at?.slice(0, 16).replace("T", " ")}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    <div className="font-display font-bold">{r.profile_name || "—"}</div>
                    <div className="text-muted-foreground">+{r.wa_id}</div>
                  </TableCell>
                  <TableCell>
                    <span className="border border-border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
                      {r.topic}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-[320px] text-xs">
                    <div>{r.summary}</div>
                    {r.trigger_text && (
                      <div className="mt-1 truncate italic text-muted-foreground">"{r.trigger_text}"</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${PRIORITY_COLOR[r.priority] || PRIORITY_COLOR.normal}`}>
                      {r.priority}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">
                    {r.status === "resolved"
                      ? <span className="text-emerald-600">✓ {r.resolved_by?.slice(0, 20) || "—"}</span>
                      : <span className="text-amber-600">Open</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Link to="/conversations">
                        <Button size="sm" variant="ghost" className="h-8 rounded-sm text-xs" data-testid={`open-chat-${r.id}`}>
                          <ExternalLink className="mr-1 h-3.5 w-3.5" /> Chat
                        </Button>
                      </Link>
                      {r.status !== "resolved" && (
                        <Button size="sm" variant="ghost" onClick={() => markResolved(r.id)} className="h-8 rounded-sm text-xs" data-testid={`resolve-${r.id}`}>
                          <Check className="mr-1 h-3.5 w-3.5 text-emerald-600" /> Resolve
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Train Bot panel — bot's own questions to admin
// ---------------------------------------------------------------------------
function TrainPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");
  const [answering, setAnswering] = useState(null); // request row
  const [answerText, setAnswerText] = useState("");
  const [sendToCustomer, setSendToCustomer] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ kind: "training" });
    if (filter !== "all") params.set("status_filter", filter);
    api.get(`/bot/requests?${params.toString()}`)
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const submitAnswer = async () => {
    if (!answerText.trim()) { toast.error("Type an answer first"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/bot/requests/${answering.id}/answer`, {
        answer: answerText.trim(),
        send_to_customer: sendToCustomer,
      });
      toast.success(r.data.sent ? "Answer saved & sent to customer" : "Answer saved to knowledge base");
      setAnswering(null);
      setAnswerText("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {["open", "resolved", "all"].map((f) => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? "default" : "outline"}
              onClick={() => setFilter(f)}
              className="h-8 rounded-sm text-xs capitalize"
              data-testid={`train-filter-${f}`}
            >
              {f}
            </Button>
          ))}
        </div>
        <div className="text-xs text-muted-foreground">
          The bot didn't know these. Answer once, and it will handle them itself next time.
        </div>
      </div>
      <div className="border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Since</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Customer</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">What the bot needs to learn</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Status</TableHead>
              <TableHead className="text-right text-[10px] font-bold uppercase tracking-[0.2em]">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="py-16 text-center text-muted-foreground">
                Nothing to train right now. When the bot hits a question it can't answer, it'll ask you here.
              </TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} className="border-border" data-testid={`train-row-${r.id}`}>
                <TableCell className="whitespace-nowrap font-mono text-[10px] text-muted-foreground">{r.opened_at?.slice(0, 16).replace("T", " ")}</TableCell>
                <TableCell className="font-mono text-xs">
                  <div className="font-display font-bold">{r.profile_name || "—"}</div>
                  <div className="text-muted-foreground">+{r.wa_id}</div>
                </TableCell>
                <TableCell className="max-w-[500px] text-xs">
                  <div className="font-medium">{r.question_for_admin || r.summary}</div>
                  {r.trigger_text && <div className="mt-1 truncate italic text-muted-foreground">"{r.trigger_text}"</div>}
                  {r.training_answer && (
                    <div className="mt-1 border-l-2 border-emerald-500 pl-2 text-emerald-700">You taught: "{r.training_answer}"</div>
                  )}
                </TableCell>
                <TableCell className="text-xs">
                  {r.status === "resolved"
                    ? <span className="text-emerald-600">✓ Taught</span>
                    : <span className="text-amber-600">Awaiting answer</span>}
                </TableCell>
                <TableCell className="text-right">
                  {r.status !== "resolved" && (
                    <Button
                      size="sm" variant="default"
                      onClick={() => { setAnswering(r); setAnswerText(""); setSendToCustomer(true); }}
                      className="h-8 rounded-sm text-xs"
                      data-testid={`teach-${r.id}`}
                    >
                      <GraduationCap className="mr-1 h-3.5 w-3.5" /> Teach answer
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!answering} onOpenChange={(o) => !o && setAnswering(null)}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle>Teach the bot</DialogTitle>
          </DialogHeader>
          {answering && (
            <div className="space-y-3">
              <div className="border border-border bg-secondary px-3 py-2 text-xs">
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Customer said</div>
                <div className="mt-1 italic">"{answering.trigger_text}"</div>
                <div className="mt-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Bot wants to know</div>
                <div className="mt-1 font-medium">{answering.question_for_admin || answering.summary}</div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em]">Your answer</Label>
                <Textarea
                  value={answerText}
                  onChange={(e) => setAnswerText(e.target.value)}
                  rows={5}
                  placeholder="Write the answer as if replying to the customer. It becomes an FAQ and the bot will reuse it."
                  className="mt-1 rounded-sm"
                  data-testid="teach-answer-input"
                />
              </div>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={sendToCustomer}
                  onChange={(e) => setSendToCustomer(e.target.checked)}
                  data-testid="teach-send-toggle"
                />
                <span>Also send this reply to +{answering.wa_id} now</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnswering(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={submitAnswer} disabled={saving || !answerText.trim()} className="rounded-sm" data-testid="teach-submit-btn">
              <Send className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save & Teach"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Knowledge base panel
// ---------------------------------------------------------------------------
function KnowledgePanel() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("faq");
  const [showAdd, setShowAdd] = useState(false);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/bot/knowledge").then((r) => setEntries(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this knowledge entry?")) return;
    try {
      await api.delete(`/bot/knowledge/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("title", f.name);
    try {
      await api.post("/bot/knowledge/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document added to knowledge base");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const faqs = entries.filter((e) => e.kind === "faq");
  const docs = entries.filter((e) => e.kind === "document");

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button size="sm" variant={tab === "faq" ? "default" : "outline"} onClick={() => setTab("faq")} className="h-8 rounded-sm text-xs" data-testid="kb-tab-faq">
            FAQs ({faqs.length})
          </Button>
          <Button size="sm" variant={tab === "doc" ? "default" : "outline"} onClick={() => setTab("doc")} className="h-8 rounded-sm text-xs" data-testid="kb-tab-doc">
            Documents ({docs.length})
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".pdf,.txt,.md" hidden onChange={upload} data-testid="kb-upload-input" />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-8 rounded-sm text-xs" data-testid="kb-upload-btn">
            <Upload className="mr-1.5 h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload PDF/TXT"}
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)} className="h-8 rounded-sm text-xs" data-testid="kb-add-btn">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Add FAQ
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>
      ) : tab === "faq" ? (
        <FaqList rows={faqs} onDelete={remove} onReload={load} />
      ) : (
        <DocList rows={docs} onDelete={remove} />
      )}

      <AddFaqDialog open={showAdd} onOpenChange={setShowAdd} onSaved={load} />
    </>
  );
}

function FaqList({ rows, onDelete }) {
  if (!rows.length) {
    return (
      <div className="border border-dashed border-border py-16 text-center text-sm text-muted-foreground" data-testid="kb-empty-faq">
        No FAQs yet. Add one, or teach the bot from the Train tab.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="kb-faq-list">
      {rows.map((f) => (
        <div key={f.id} className="border border-border bg-background p-4" data-testid={`kb-faq-${f.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                Q · {SOURCE_LABEL[f.source] || f.source}
              </div>
              <div className="mt-1 font-medium">{f.question}</div>
              <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">A</div>
              <div className="mt-1 whitespace-pre-wrap text-sm text-neutral-800">{f.answer}</div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => onDelete(f.id)} className="h-8 rounded-sm text-xs" data-testid={`kb-faq-delete-${f.id}`}>
              <Trash2 className="h-3.5 w-3.5 text-red-500" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function DocList({ rows, onDelete }) {
  if (!rows.length) {
    return (
      <div className="border border-dashed border-border py-16 text-center text-sm text-muted-foreground" data-testid="kb-empty-doc">
        No documents yet. Upload a PDF or TXT to give the bot reference material.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="kb-doc-list">
      {rows.map((d) => (
        <details key={d.id} className="border border-border bg-background p-4" data-testid={`kb-doc-${d.id}`}>
          <summary className="flex cursor-pointer items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                Document · {SOURCE_LABEL[d.source] || d.source}
              </div>
              <div className="mt-1 font-medium">{d.title || "Untitled"}</div>
              <div className="text-[11px] text-muted-foreground">{(d.content || "").length.toLocaleString()} characters</div>
            </div>
            <Button size="sm" variant="ghost" onClick={(e) => { e.preventDefault(); onDelete(d.id); }} className="h-8 rounded-sm text-xs" data-testid={`kb-doc-delete-${d.id}`}>
              <Trash2 className="h-3.5 w-3.5 text-red-500" />
            </Button>
          </summary>
          <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap border border-border bg-secondary p-3 font-mono text-[11px] text-neutral-700">
            {d.content}
          </pre>
        </details>
      ))}
    </div>
  );
}

function AddFaqDialog({ open, onOpenChange, onSaved }) {
  const [q, setQ] = useState("");
  const [a, setA] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!q.trim() || !a.trim()) { toast.error("Both question and answer required"); return; }
    setSaving(true);
    try {
      await api.post("/bot/knowledge", { kind: "faq", question: q.trim(), answer: a.trim() });
      toast.success("FAQ added");
      setQ(""); setA("");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-sm">
        <DialogHeader>
          <DialogTitle>Add FAQ</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em]">Question (what a customer might ask)</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} className="mt-1 rounded-sm" data-testid="faq-q-input" placeholder="e.g. What are your clinic timings?" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em]">Answer</Label>
            <Textarea value={a} onChange={(e) => setA(e.target.value)} rows={5} className="mt-1 rounded-sm" data-testid="faq-a-input" placeholder="e.g. We're open Mon-Sat, 10 AM to 8 PM. Sundays closed." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm" data-testid="faq-save-btn">
            <Plus className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Add FAQ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Settings panel — now with tone
// ---------------------------------------------------------------------------
function SettingsPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/bot/settings").then((r) => setS(r.data)); }, []);

  if (!s) return <div className="text-muted-foreground">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/bot/settings", s);
      setS(r.data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div className="border border-border p-4">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={s.enabled}
            onChange={(e) => setS({ ...s, enabled: e.target.checked })}
            data-testid="bot-enabled-toggle"
            className="h-5 w-5"
          />
          <div>
            <div className="font-display text-lg font-bold">Auto-responder enabled</div>
            <div className="text-xs text-muted-foreground">Master switch. When off, no auto-replies are sent and nothing is written to Requests.</div>
          </div>
        </label>
      </div>

      <div>
        <Label className="text-xs font-bold uppercase tracking-[0.2em]">Tone / personality</Label>
        <Textarea
          value={s.tone || ""}
          onChange={(e) => setS({ ...s, tone: e.target.value })}
          rows={3}
          className="mt-1 rounded-sm text-sm"
          data-testid="bot-tone-input"
          placeholder="e.g. Warm and polite. Always end with '- Team Eden'."
        />
        <p className="mt-1 text-[11px] text-muted-foreground">Applied on top of the system prompt. Keep it short.</p>
      </div>

      <div>
        <Label className="text-xs font-bold uppercase tracking-[0.2em]">System prompt (Claude's brief)</Label>
        <Textarea
          value={s.system_prompt}
          onChange={(e) => setS({ ...s, system_prompt: e.target.value })}
          rows={12}
          className="mt-1 rounded-sm font-mono text-xs"
          data-testid="bot-prompt-input"
        />
        <p className="mt-1 text-[11px] text-muted-foreground">
          Tell the bot who you are, what it can answer, and what it must escalate. Keep the JSON schema instructions at the end — the code parses the JSON response.
        </p>
      </div>

      <div className="border border-border p-4">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={s.biz_hours_suppress}
            onChange={(e) => setS({ ...s, biz_hours_suppress: e.target.checked })}
            data-testid="bot-biz-suppress-toggle"
          />
          <div>
            <div className="font-bold">Suppress during business hours</div>
            <div className="text-xs text-muted-foreground">When enabled, bot only replies OUTSIDE the window below (so it never steps on live staff).</div>
          </div>
        </label>
        <div className="mt-3 flex items-center gap-2 text-xs">
          <Clock className="h-3.5 w-3.5" /> IST business hours:
          <Input
            type="number" min={0} max={23}
            value={s.biz_start_hour_ist}
            onChange={(e) => setS({ ...s, biz_start_hour_ist: parseInt(e.target.value, 10) || 0 })}
            className="h-8 w-16 rounded-sm text-center"
            data-testid="bot-start-hour"
          />
          <span>to</span>
          <Input
            type="number" min={0} max={23}
            value={s.biz_end_hour_ist}
            onChange={(e) => setS({ ...s, biz_end_hour_ist: parseInt(e.target.value, 10) || 0 })}
            className="h-8 w-16 rounded-sm text-center"
            data-testid="bot-end-hour"
          />
        </div>
      </div>

      <div>
        <Label className="text-xs font-bold uppercase tracking-[0.2em]">Staff-reply grace (minutes)</Label>
        <Input
          type="number" min={1} max={60}
          value={s.staff_reply_grace_min}
          onChange={(e) => setS({ ...s, staff_reply_grace_min: parseInt(e.target.value, 10) || 5 })}
          className="mt-1 w-32 rounded-sm"
          data-testid="bot-grace-input"
        />
        <p className="mt-1 text-[11px] text-muted-foreground">Bot won't reply if a human sent anything in this chat within the last N minutes.</p>
      </div>

      <Button onClick={save} disabled={saving} className="rounded-sm" data-testid="save-bot-settings-btn">
        <Sparkles className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save settings"}
      </Button>
    </div>
  );
}

function ActivityPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/bot/activity?limit=200").then((r) => setRows(r.data)); }, []);
  return (
    <div className="border border-border">
      <Table>
        <TableHeader>
          <TableRow className="border-border">
            <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">When</TableHead>
            <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Chat</TableHead>
            <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Action</TableHead>
            <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Input</TableHead>
            <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Output / Reason</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow><TableCell colSpan={5} className="py-16 text-center text-muted-foreground">No bot activity yet.</TableCell></TableRow>
          ) : rows.map((a) => (
            <TableRow key={a.id} className="border-border">
              <TableCell className="whitespace-nowrap font-mono text-[10px] text-muted-foreground">
                {a.at?.slice(0, 19).replace("T", " ")}
              </TableCell>
              <TableCell className="font-mono text-xs">+{a.wa_id}</TableCell>
              <TableCell>
                <span className="border border-border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
                  {a.action}
                </span>
              </TableCell>
              <TableCell className="max-w-[240px] truncate text-xs italic">"{a.input_text}"</TableCell>
              <TableCell className="max-w-[320px] text-xs">
                {a.output_text || <span className="text-muted-foreground">{a.reason || "—"}</span>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
